import React, { useState, useEffect } from 'react';
import GaugeChart from '../ui/gauge';
import { Table, Progress, Tooltip, Alert, Skeleton, Card } from 'antd';
import { Card as CustomCard, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, BarChart, Building, UserPlus, Calendar, CreditCard, TrendingUp, Percent, Target, Briefcase, ArrowRight, CheckCircle, PieChart, Gauge } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_URL, SALES_API_URL } from '../../env';
import Cookies from 'js-cookie';
import { getAccessTokenOrRedirect } from '../../utils/auth';
import { useAuth } from '../../providers/AuthProvider';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';
import KpiTable from './KpiTable';
import { KpiResponse } from './types';

// Define types for our data
interface InputsStats {
  total_companies: number;
  total_personal_target: number;
  target_achieved_percent: number;
  ytd_target: number;
  day_of_year: number;
  total_days_in_year: number;
  pipeline_stages: PipelineStages;
  new_clients_ytd: number;
  sales_prospect_calculator: SalesProspectCalculator;
  total_company_target?: number;
  total_company_achievement?: number;
}

interface PipelineStages {
  total_companies: number;
  enquiry_stage_count: number;
  quotation_stage_count: number;
  sales_order_stage_count: number;
  invoice_stage_count: number;
}

interface SalesProspectCalculator {
  ytd_enquiry: number;
  no_of_prospect_on_table: number;
  no_of_invoice: number;
  ytd_achieved: number;
  target: number;
  balance_target: number;
  avg_invoice_val: number;
  conversion_ratio: number;
  no_of_invoice_required: number;
  no_of_prospects_year: number;
  no_of_prospects_require: number;
  prospects_shortfall: number;
}

interface CompanyStageData {
  name: string;
  value: number;
  color: string;
}

interface CustomerTarget {
  key?: number;
  customer_id: number;
  customer_name: string;
  target: number;
  achievement: number;
  progress?: number;
}

interface CustomerTargetsResponse {
  results: CustomerTarget[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
}

interface InputsSectionProps {
  selectedUser: number | null;
  dateRange: { startDate: string; endDate: string };
}

// Responsive Speedometer Component
const Speedometer: React.FC<{
  value: number;
  maxValue?: number;
  title: string;
  currencySymbol: string;
  ytdTarget: number;
  ytdAchieved: number;
}> = ({ value, maxValue = 100, title, currencySymbol, ytdTarget, ytdAchieved }) => {
  const clampedValue = Math.max(0, Math.min(value, maxValue));
  const progressRatio = maxValue ? clampedValue / maxValue : 0;

  const formattedTarget = `${currencySymbol}${ytdTarget.toLocaleString()}`;
  const formattedAchieved = `${currencySymbol}${ytdAchieved.toLocaleString()}`;

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="flex flex-col items-center gap-6">
        <GaugeChart
          percent={progressRatio}
          centerText="Achievement"
          centerTextColor="#2956e863"
          centerTextSize="2.5rem"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
          <div className="rounded-lg bg-slate-50 p-4 text-left">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">YTD Target</p>
            <p className="mt-1 text-base font-semibold text-slate-900">{formattedTarget}</p>
          </div>
          <div className="rounded-lg bg-emerald-50 p-4 text-left">
            <p className="text-xs font-medium uppercase tracking-wide text-emerald-600">YTD Achieved</p>
            <p className="mt-1 text-base font-semibold text-emerald-700">{formattedAchieved}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const InputsSection: React.FC<InputsSectionProps> = ({ selectedUser, dateRange }) => {
  const { userInfo } = useAuth();
  const currencySymbol = userInfo?.company_base_currency_symbol || '$';

  // State for API data
  const [inputsStats, setInputsStats] = useState<InputsStats | null>(null);
  const [kpiData, setKpiData] = useState<KpiResponse | null>(null);
  const [customerTargets, setCustomerTargets] = useState<CustomerTarget[]>([]);
  const [companyStagesData, setCompanyStagesData] = useState<CompanyStageData[]>([]);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
    totalPages: 0
  });

  // Loading and error states
  const [statsLoading, setStatsLoading] = useState(true);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [targetsLoading, setTargetsLoading] = useState(true);
  const [prospectStatsLoading, setProspectStatsLoading] = useState(true);
  const [companyStagesLoading, setCompanyStagesLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [kpiError, setKpiError] = useState<string | null>(null);
  const [targetsError, setTargetsError] = useState<string | null>(null);
  const [prospectStatsError, setProspectStatsError] = useState<string | null>(null);
  const [companyStagesError, setCompanyStagesError] = useState<string | null>(null);

  // Fetch inputs stats data
  useEffect(() => {
    if (!userInfo) return;

    const fetchInputsStats = async () => {
      try {
        setStatsLoading(true);
        setStatsError(null);
        // Clear old data immediately when starting new fetch
        setInputsStats(null);

        const jwt = getAccessTokenOrRedirect(Cookies.get.bind(Cookies));

        // Build query parameters using URLSearchParams to avoid injection issues
        const params = new URLSearchParams();
        if (selectedUser) {
          params.append('user_id', selectedUser.toString());
        }
        if (dateRange) {
          params.append('start_date', dateRange.startDate);
          params.append('end_date', dateRange.endDate);
        }

        const response = await axios.get(`${API_URL}/api/inputs/`, {
          params,
          headers: {
            'Authorization': `Bearer ${jwt}`
          }
        });

        setInputsStats(response.data);
      } catch (err) {
        console.error('Error fetching inputs stats:', err);
        setStatsError('Failed to load inputs statistics');
        setInputsStats(null);
      } finally {
        setStatsLoading(false);
      }
    };
    const fetchKpiData = async () => {
      try {
        setKpiLoading(true);
        setKpiError(null);
        setKpiData(null);

        const baseUrl = SALES_API_URL || API_URL;
        if (!baseUrl) {
          throw new Error('KPI API URL is not configured');
        }

        const jwt = getAccessTokenOrRedirect(Cookies.get.bind(Cookies));
        const resolvedUserId = selectedUser ?? userInfo?.userId;

        if (!resolvedUserId) {
          throw new Error('User information is required to load KPI data');
        }

        const month = dateRange?.endDate
          ? dayjs(dateRange.endDate).format('MM')
          : dayjs().format('MM');

        const response = await axios.post<KpiResponse>(
          `${baseUrl}/api/config/kpi/sync`,
          {
            user_id: resolvedUserId.toString(),
            month,
            updateHRMS: false,
          },
          {
            headers: {
              Authorization: `Bearer ${jwt}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.data?.success !== 1 || !Array.isArray(response.data?.data)) {
          throw new Error('Invalid KPI response');
        }

        setKpiData(response.data);
      } catch (err) {
        console.error('Error fetching KPI data:', err);
        setKpiError('Failed to load KPI data');
        setKpiData(null);
      } finally {
        setKpiLoading(false);
      }
    };

    fetchInputsStats();
    fetchKpiData();
  }, [selectedUser, userInfo, dateRange]); // Re-fetch when user or selection changes

  // When inputs stats are loaded, prepare company stages data and sales prospect calculator data
  useEffect(() => {
    if (inputsStats) {
      // Handle company stages data
      if (inputsStats.pipeline_stages) {
        const { pipeline_stages } = inputsStats;

        setCompanyStagesData([
          { name: 'Enquiry', value: pipeline_stages.enquiry_stage_count, color: '#38bdf8' },
          { name: 'Quotation', value: pipeline_stages.quotation_stage_count, color: '#a78bfa' },
          { name: 'Sales Order', value: pipeline_stages.sales_order_stage_count, color: '#4ade80' },
          { name: 'Invoice', value: pipeline_stages.invoice_stage_count, color: '#f87171' }
        ]);

        setCompanyStagesLoading(false);
      }

      // Handle sales prospect calculator data
      if (inputsStats.sales_prospect_calculator) {
        setProspectStatsLoading(false);
      }
    } else {
      // Clear data when inputsStats is null (during loading)
      setCompanyStagesData([]);
      setCompanyStagesLoading(true);
      setProspectStatsLoading(true);
    }
  }, [inputsStats]);

  // Fetch customer targets data with pagination
  useEffect(() => {
    // Similar guard as above to avoid initial request without user context
    if (!userInfo) return;

    const fetchCustomerTargets = async () => {
      try {
        setTargetsLoading(true);
        setTargetsError(null);
        // Clear old data immediately when starting new fetch (except for pagination changes)
        if (pagination.current === 1) {
          setCustomerTargets([]);
        }

        const jwt = getAccessTokenOrRedirect(Cookies.get.bind(Cookies));

        // Prepare query parameters, including user_id if a specific user is selected
        const params: any = {
          page: pagination.current,
          page_size: pagination.pageSize
        };

        if (selectedUser) {
          params.user_id = selectedUser;
        }

        if (dateRange) {
          params.start_date = dateRange.startDate;
          params.end_date = dateRange.endDate;
        }

        if (sortField && sortOrder) {
          params.sort_field = sortField;
          params.sort_order = sortOrder === 'ascend' ? 'ASC' : 'DESC';
        }

        const response = await axios.get(`${API_URL}/api/customer-targets/`, {
          params,
          headers: {
            'Authorization': `Bearer ${jwt}`
          }
        });

        const data: CustomerTargetsResponse = response.data;

        // Map API data to our component data structure
        const processedTargets = data.results

        setCustomerTargets(processedTargets);
        setPagination({
          current: data.pagination.page,
          pageSize: data.pagination.page_size,
          total: data.pagination.total_count,
          totalPages: data.pagination.total_pages
        });
      } catch (err) {
        console.error('Error fetching customer targets:', err);
        setTargetsError('Failed to load customer targets');
        setCustomerTargets([]);
      } finally {
        setTargetsLoading(false);
      }
    };

    fetchCustomerTargets();
  }, [pagination.current, pagination.pageSize, selectedUser, userInfo, dateRange, sortField, sortOrder]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    // Handle sorting
    if (sorter) {
      let newField: string | null = null;
      let newOrder: 'ascend' | 'descend' | null = null;

      if (!Array.isArray(sorter)) {
        if (sorter.field && sorter.order) {
          newField = sorter.field.toString();
          newOrder = sorter.order;
        }
      } else if (sorter.length > 0 && sorter[0].field) {
        newField = sorter[0].field.toString();
        newOrder = sorter[0].order;
      }

      setSortField(newField);
      setSortOrder(newOrder);
    }
    setPagination(prev => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize
    }));
  };

  // Calculate YTD percentage
  const ytdTargetPercentage = inputsStats && inputsStats.target_achieved_percent || 0;

  // Custom pie chart label renderer
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Table columns configuration
  const columns: ColumnsType<CustomerTarget> = [
    {
      title: targetsLoading ? <Skeleton.Input active size="small" style={{ width: 80 }} /> : 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      sorter: true,
      width: 400,
      ellipsis: true,
      render: (text: string) => targetsLoading ? <Skeleton.Input active size="small" /> : (
        <Tooltip title={text} placement="topLeft">
          {text}
        </Tooltip>
      ),
    },
    {
      title: targetsLoading ? <Skeleton.Input active size="small" style={{ width: 60 }} /> : 'Target',
      dataIndex: 'target',
      key: 'target',
      render: (value: number) => targetsLoading ? <Skeleton.Input active size="small" /> : `${currencySymbol}${value.toLocaleString()}`,
      sorter: true,
    },
    {
      title: targetsLoading ? <Skeleton.Input active size="small" style={{ width: 70 }} /> : 'Achieved',
      dataIndex: 'achievement',
      key: 'achievement',
      render: (value: number) => targetsLoading ? <Skeleton.Input active size="small" /> : `${currencySymbol}${value.toLocaleString()}`,
      sorter: true,
    },
    {
      title: targetsLoading ? <Skeleton.Input active size="small" style={{ width: 70 }} /> : 'Progress',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress: number) => {
        if (targetsLoading) {
          return <div className="flex items-center gap-2">
            <Skeleton.Input active size="small" style={{ width: 250, height: 16 }} />
            <div className="animate-pulse h-6 w-6 bg-slate-100 rounded"></div>
          </div>;
        }
        return (
          <Tooltip title={`${progress}%`}>
            <Progress
              percent={progress}
              size="small"
              strokeColor={
                progress >= 90 ? '#10b981' :
                  progress >= 70 ? '#3b82f6' :
                    progress >= 50 ? '#f59e0b' :
                      '#ef4444'
              }
            />
          </Tooltip>
        );
      },
      sorter: true,
    },
  ];

  const createSkeletonCustomerTargets = (): CustomerTarget[] => {
    return Array.from({ length: pagination.pageSize }, (_, index) => ({
      key: index,
      customer_id: 0,
      customer_name: '',
      target: 0,
      achievement: 0,
      progress: 0
    }));
  };

  return (
    <CustomCard className="rounded-lg border shadow-sm">
      <CardHeader className="px-4 pt-6 pb-0 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <CardTitle className="flex items-center gap-2 text-xl font-semibold">
            {statsLoading || targetsLoading || prospectStatsLoading || companyStagesLoading ? (
              <Skeleton.Input active size="small" style={{ width: 120, height: 25 }} />
            ) : (
              'Inputs'
            )}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-6 sm:px-6">
        {/* Stats Cards */}
        {statsError && (
          <Alert message={statsError} type="error" showIcon className="mb-4 text-left" />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-6">
          {/* Companies Assigned */}
          <Card className="bg-blue-50 border-0">
            {statsLoading ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <div className="animate-pulse"></div>
                  </div>
                  <span className="text-sm font-medium text-blue-600">
                    <div className="animate-pulse h-4 w-24 bg-blue-100 rounded"></div>
                  </span>

                </div>
                <div className="animate-pulse">
                  <div className="h-8 w-24 bg-blue-100 rounded mb-2" />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-blue-600">
                    Companies Assigned
                  </span>
                </div>
                <div className="mt-8 text-blue-600">
                  <div className="flex items-center justify-center gap-2 text-3xl font-semibold">
                    <span>{inputsStats?.total_companies || 0}</span>
                  </div>
                </div>

              </>
            )}
          </Card>

          {/* Total Target */}
          <Card className="bg-emerald-50 border-0">
            {statsLoading ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <div className="animate-pulse"></div>
                  </div>
                  <span className="text-sm font-medium text-emerald-600">
                    <div className="animate-pulse h-4 w-24 bg-emerald-100 rounded"></div>
                  </span>
                </div>
                <div className="animate-pulse">
                  <div className="h-8 w-24 bg-emerald-100 rounded mb-2" />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-emerald-600">
                    Total Target
                  </span>
                </div>
                <div>
                  <div className="flex items-center justify-center mt-8">
                    <span className="text-3xl font-semibold text-slate-900">
                      {currencySymbol}{(inputsStats?.total_personal_target || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </>
            )}
          </Card>

          {/* YTD Target */}
          <Card className="bg-violet-50 border-0">
            {statsLoading ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <div className="animate-pulse"></div>
                  </div>
                  <span className="text-sm font-medium text-violet-600">
                    <div className="animate-pulse h-4 w-24 bg-violet-100 rounded"></div>
                  </span>
                </div>
                <div className="animate-pulse">
                  <div className="h-8 w-24 bg-violet-100 rounded mb-2" />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-4 w-4 text-violet-600" />
                  </div>
                  <span className="text-sm font-medium text-violet-600">
                    YTD Target
                  </span>
                </div>
                <div>
                  <div className="flex items-center justify-center mt-8">
                    <span className="text-3xl font-semibold text-slate-900">
                      {currencySymbol}{(inputsStats?.ytd_target || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </>
            )}
          </Card>

          {/* YTD Achievement */}
          <Card className="bg-indigo-50 border-0">
            {statsLoading ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <div className="animate-pulse"></div>
                  </div>
                  <span className="text-sm font-medium text-indigo-600">
                    <div className="animate-pulse h-4 w-24 bg-indigo-100 rounded"></div>
                  </span>
                </div>
                <div className="animate-pulse">
                  <div className="h-8 w-24 bg-indigo-100 rounded mb-2" />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart className="h-4 w-4 text-indigo-600" />
                  </div>
                  <span className="text-sm font-medium text-indigo-600">
                    YTD Achievement
                  </span>
                </div>
                <div>
                  <div className="flex items-center justify-center mt-4">
                    <span className="text-3xl font-semibold text-slate-900">
                      {ytdTargetPercentage}%
                    </span>
                  </div>
                  <div className="mt-2">
                    <Progress percent={ytdTargetPercentage} size="small" strokeColor="#6366f1" />
                  </div>
                </div>
              </>
            )}
          </Card>

          {/* New Customers Onboarded */}
          <Card className="bg-amber-50 border-0">
            {statsLoading ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <div className="animate-pulse"></div>
                  </div>
                  <span className="text-sm font-medium text-amber-600">
                    <div className="animate-pulse h-4 w-24 bg-amber-100 rounded"></div>
                  </span>
                </div>
                <div className="animate-pulse">
                  <div className="h-8 w-24 bg-amber-100 rounded mb-2" />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <UserPlus className="h-4 w-4 text-amber-600" />
                  </div>
                  <span className="text-sm font-medium text-amber-600">
                    New Customers Onboarded
                  </span>
                </div>
                <div>
                  <div className="flex items-center justify-center mt-8">
                    <span className="text-3xl font-semibold text-slate-900">
                      {inputsStats?.new_clients_ytd || 0}
                    </span>
                  </div>
                </div>
              </>
            )}
          </Card>
        </div>

        {/* YTD Performance Speedometer */}
        <Card
          title={statsLoading ? (
            <div className="flex items-center gap-2 text-lg font-semibold text-left">
              <div className="h-8 w-8 bg-slate-200 rounded-full animate-pulse"></div>
              <div className="h-4 w-60 bg-slate-200 rounded animate-pulse"></div>
            </div>
          ) : (

            <div className="flex items-center gap-2 text-lg font-semibold text-left">
              <Gauge className="h-5 w-5 text-slate-900" />
              <span>Key Performance Indicators</span>
            </div>
          )
          }
          className="mb-6 text-left"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
            {statsLoading ? (
              <div className="flex justify-center">
                <div className="h-64 w-64 bg-slate-200 rounded-full" ></div>
              </div>
            ) : (
              <div className="flex justify-center">
                <Speedometer
                  value={ytdTargetPercentage}
                  title="Sales Performance"
                  currencySymbol={currencySymbol}
                  ytdTarget={inputsStats?.ytd_target || 0}
                  ytdAchieved={inputsStats?.sales_prospect_calculator?.ytd_achieved || 0}
                />
              </div>
            )}
            <KpiTable
              loading={kpiLoading}
              error={kpiError}
              kpiData={kpiData}
            />
          </div>
        </Card>

        {/* Sales Prospect Calculator and Company Stages Pie Chart - Side by Side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 mb-6">
          {/* Sales Prospect Calculator */}
          <Card
            title={(prospectStatsLoading || !inputsStats?.sales_prospect_calculator) ? (
              <div className="flex items-center gap-2 text-lg font-semibold text-left">
                <div className="h-8 w-8 bg-slate-200 rounded-full animate-pulse"></div>
                <div className="h-4 w-60 bg-slate-200 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-lg font-semibold text-left">
                <Target className="h-5 w-5 text-slate-900" />
                <span>Sales Prospect Calculator</span>
              </div>
            )}
            className="text-left"
          >
            {prospectStatsError && (
              <Alert message={prospectStatsError} type="error" showIcon className="mb-4 text-left" />
            )}

            {prospectStatsLoading || !inputsStats?.sales_prospect_calculator ? (
              <div className="animate-pulse">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {[...Array(9)].map((_, i) => (
                    <div key={i} className="flex flex-col">
                      <div className="h-4 w-48 bg-slate-200 rounded mb-2" />
                      <div className="h-8 w-24 bg-slate-200 rounded" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-600">
                      No. of (Closed sales) Invoices till date
                    </span>
                  </div>
                  <span className="text-xl font-semibold text-slate-900">
                    {inputsStats.sales_prospect_calculator.no_of_invoice}
                  </span>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-600">
                      Avg. (Closed sales) Invoice Value
                    </span>
                  </div>
                  <span className="text-xl font-semibold text-slate-900">
                    {currencySymbol}{inputsStats.sales_prospect_calculator.avg_invoice_val.toLocaleString()}
                  </span>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-600">
                      No. of Invoices (Closed sales) Required
                    </span>
                  </div>
                  <span className="text-xl font-semibold text-slate-900">
                    {Math.ceil(inputsStats.sales_prospect_calculator.no_of_invoice_required)}
                  </span>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Percent className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-600">
                      Conversion Ratio (%)
                    </span>
                  </div>
                  <span className="text-xl font-semibold text-slate-900">
                    {inputsStats.sales_prospect_calculator.conversion_ratio.toFixed(2)}%
                  </span>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-600">
                      No. of Prospects Per Year
                    </span>
                  </div>
                  <span className="text-xl font-semibold text-slate-900">
                    {Math.ceil(inputsStats.sales_prospect_calculator.no_of_prospects_year)}
                  </span>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-600">
                      No. of Prospects on the table
                    </span>
                  </div>
                  <span className="text-xl font-semibold text-slate-900">
                    {inputsStats.sales_prospect_calculator.no_of_prospect_on_table}
                  </span>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-600">
                      No. of Prospects Required
                    </span>
                  </div>
                  <span className="text-xl font-semibold text-slate-900">
                    {Math.ceil(inputsStats.sales_prospect_calculator.no_of_prospects_require)}
                  </span>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowRight className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-600">
                      No. of Prospects Shortfall
                    </span>
                  </div>
                  <span className={`text-xl font-semibold ${inputsStats.sales_prospect_calculator.prospects_shortfall <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {inputsStats.sales_prospect_calculator.prospects_shortfall}
                  </span>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-600">
                      YTD Achieved
                    </span>
                  </div>
                  <span className="text-xl font-semibold text-slate-900">
                    {currencySymbol}{inputsStats.sales_prospect_calculator.ytd_achieved.toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </Card>

          {/* Company Stages Pie Chart */}
          <Card
            title={(companyStagesLoading || !inputsStats?.pipeline_stages) ? (
              <div className="flex items-center gap-2 text-lg font-semibold text-left">
                <div className="h-8 w-8 bg-slate-200 rounded-full animate-pulse"></div>
                <div className="h-4 w-40 bg-slate-200 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-lg font-semibold text-left">
                <PieChart className="h-5 w-5 text-slate-900" />
                <span>Companies by Stages</span>
              </div>
            )
            }
            className="text-left"
          >
            {companyStagesError && (
              <Alert message={companyStagesError} type="error" showIcon className="mb-4 text-left" />
            )}

            {companyStagesLoading || !inputsStats?.pipeline_stages ? (
              <div className="animate-pulse">
                <div className="flex justify-center">
                  <div className="h-64 w-64 bg-slate-200 rounded-full mb-4" />
                </div>
                <div className="flex justify-center gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="h-4 w-4 bg-slate-300 rounded" />
                      <div className="h-4 w-16 bg-slate-200 rounded" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row items-center justify-center">
                <div className="w-full md:w-3/5 h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={companyStagesData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {companyStagesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Legend layout="vertical" verticalAlign="middle" align="right" />
                      <RechartsTooltip
                        formatter={(value: any, name: any) => [`${value} Companies`, name]}
                        labelFormatter={() => 'Stage'}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>

                <div className="w-full md:w-2/5 mt-4 md:mt-0">
                  <div className="grid grid-cols-1 gap-3">
                    {companyStagesData.map((stage, index) => (
                      <div key={index} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: stage.color }} />
                          <span className="text-sm font-medium">{stage.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">{stage.value}</span>
                          <span className="text-xs text-slate-500">Companies</span>
                        </div>
                      </div>
                    ))}
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mt-2 pt-2 border-t border-slate-200">
                      <span className="text-sm font-medium">Total</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">
                          {companyStagesData.reduce((sum, item) => sum + item.value, 0)}
                        </span>
                        <span className="text-xs text-slate-500">Companies</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Customer Wise Target Table */}
        <Card
          title={
            targetsLoading ? (
              <Skeleton.Input style={{ width: 120, height: 25 }} active />
            ) : (
              <h3 className="text-lg font-semibold text-left text-slate-900 text-left">Customer Wise Target</h3>
            )
          }
          className="mt-4 text-left"
        >
          {targetsError && (
            <Alert message={targetsError} type="error" showIcon className="mb-4 text-left" />
          )}

          <div className="overflow-x-auto">
            <Table
              columns={columns}
              dataSource={targetsLoading ? createSkeletonCustomerTargets() : customerTargets}
              loading={false}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ['5', '10', '20', '50'],
              }}
              onChange={handleTableChange}
              size="middle"
            />
          </div>
        </Card>
      </CardContent >
    </CustomCard >
  );
};

export default InputsSection;
